import React, { useState, useEffect } from 'react';
import { Tooltip } from 'primereact/tooltip';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { ColorPicker } from 'primereact/colorpicker';
import { useGetConfigurationQuery, useUpdateConfigurationMutation } from '../services/configApi';
import { useToast } from "../hooks/useToast.ts";

// Funkcja do automatycznego dobierania kontrastowego koloru tekstu
function getContrastColor(hexColor) {
    if (!hexColor) return '#000000';
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#000000' : '#ffffff';
}

export default function LegendColorPicker({ onColorSelect, selectedColor: initialSelectedColor }) {
    const [selectedColor, setSelectedColor] = useState(initialSelectedColor || null);
    const [legend, setLegend] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const toast = useToast();

    const { data, isLoading, error } = useGetConfigurationQuery();
    const [updateConfiguration, { isLoading: isUpdating }] = useUpdateConfigurationMutation();

    useEffect(() => {
        if (data?.legend) {
                // sort legend by `code` key for consistent ordering
                const sortLegendByCode = (a, b) => {
                    const codeA = (a.code || '').toString().toLowerCase();
                    const codeB = (b.code || '').toString().toLowerCase();
                    // ensure the special code 'brak' is always last
                    if (codeA === 'brak' && codeB === 'brak') return 0;
                    if (codeA === 'brak') return 1;
                    if (codeB === 'brak') return -1;
                    return codeA.localeCompare(codeB);
                };
                const sorted = [...data.legend].sort(sortLegendByCode);
                setLegend(sorted);
            }
    }, [data]);

    useEffect(() => {
        if (initialSelectedColor !== undefined) {
            setSelectedColor(initialSelectedColor);
        }
    }, [initialSelectedColor]);

    const handleSelect = (color) => {
        setSelectedColor(color);
        onColorSelect?.(color);
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleLegendChange = (index, field, value) => {
        if (field === 'background' && value[0] !== '#') {
            value = `#${value}`;
        }
        const newLegend = [...legend];
        newLegend[index] = { ...newLegend[index], [field]: value };
        setLegend(newLegend);
    };

    const handleAdd = () => {
        setLegend([...legend, { name: '', code: '', background: '#ffffff' }]);
    };

    const handleRemove = (index) => {
        const newLegend = [...legend];
        newLegend.splice(index, 1);
        setLegend(newLegend);
    };

    const handleSave = async () => {
        try {
            // ensure legend is sorted by `code` before saving; keep 'Brak' at the end
            const sortLegendByCode = (a, b) => {
                const codeA = (a.code || '').toString().toLowerCase();
                const codeB = (b.code || '').toString().toLowerCase();
                if (codeA === 'brak' && codeB === 'brak') return 0;
                if (codeA === 'brak') return 1;
                if (codeB === 'brak') return -1;
                return codeA.localeCompare(codeB);
            };
            const sortedLegend = [...legend].sort(sortLegendByCode);
            await updateConfiguration(sortedLegend).unwrap();
            setIsModalOpen(false);
            toast(`Aktualizacja legendy`, "info");
        } catch (err) {
            toast(`Błąd podczas aktualizacji legendy: ${err.message}`, "error");

        }
    };

    if (isLoading) return <div>Ładowanie legendy...</div>;
    if (error) return <div>Błąd podczas ładowania legendy</div>;

    return (
        <div>
            <Tooltip target="[data-pr-tooltip]" />
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold' }}>
                Wybierz kolor z legendy:
            </label>

            {/* Wyświetlanie kafelków z legendą */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                {legend.map((item) => (
                    <div
                        key={item.code + item.name}
                        onClick={() => handleSelect(item.background)}
                        style={{
                            width: '60px',
                            height: '60px',
                            backgroundColor: item.background,
                            border: selectedColor === item.background ? '3px solid #000' : '1px solid #ccc',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: getContrastColor(item.background),
                            fontSize: '10px',
                            fontWeight: 'bold',
                            borderRadius: '8px',
                            transition: 'transform 0.1s ease',
                        }}
                        data-pr-tooltip={`${item.name} (${item.code})`}
                        onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                        onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1.0)')}
                    >
                        {item.code}
                    </div>
                ))}
            </div>

            <Button label="Edytuj legendę" icon="pi pi-pencil" onClick={handleOpenModal} />

            {/* Modal z tabelą */}
            <Dialog
                header="Edytuj legendę"
                visible={isModalOpen}
                style={{ width: '700px' }}
                modal
                onHide={handleCloseModal}
            >
                <table className="p-datatable p-component" style={{ width: '100%' }}>
                    <thead>
                        <tr>
                            <th style={{ width: '50%' }}>Nazwa</th>
                            <th style={{ width: '40%' }}>Skrót</th>
                            <th style={{ width: '8%' }}>Tło</th>
                            <th style={{ width: '2%' }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {legend.map((item, index) => (
                            <tr key={index}>
                                <td>
                                    <InputText
                                        value={item.name}
                                        placeholder="Nazwa"
                                        style={{ width: '100%' }}
                                        onChange={(e) => handleLegendChange(index, 'name', e.target.value)}
                                    />
                                </td>
                                <td>
                                    <InputText
                                        value={item.code}
                                        placeholder="Skrót"
                                        style={{ width: '100%' }}
                                        onChange={(e) => handleLegendChange(index, 'code', e.target.value)}
                                    />
                                </td>
                                <td align='center'>
                                    <ColorPicker
                                        value={item.background}
                                        onChange={(e) => handleLegendChange(index, 'background', e.value)}
                                        style={{ width: '100%' }}
                                        format="hex"
                                    />
                                </td>
                                <td>
                                    <Button
                                        icon="pi pi-trash"
                                        className="p-button-danger p-button-text"
                                        onClick={() => handleRemove(index)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                    <Button label="Dodaj element" icon="pi pi-plus" onClick={handleAdd} />
                </div>
                <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <Button label="Anuluj" className="p-button-secondary" onClick={handleCloseModal} />
                    <Button label="Zapisz" onClick={handleSave} loading={isUpdating} />
                </div>
            </Dialog>
        </div>
    );
}
